import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import HeaderSocials from "./HeaderSocials";
import "./header.css";
import { getFileUrl } from "../../sanityClient";

gsap.registerPlugin(ScrollTrigger);

const Header = ({ intro }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;

    if (!video || !container) return;

    let ctx;

    const handleLoadedMetadata = () => {
      if (!video.duration || isNaN(video.duration)) return;

      video.pause();

      ctx = gsap.context(() => {
        ScrollTrigger.create({
          trigger: container,
          start: "top top",
          end: "500vh", // Increased to 250vh for slower scroll
          pin: ".video-wrapper",
          pinSpacing: false,
          scrub: 1000, // Slightly more smoothing
          anticipatePin: 1,
          onUpdate: (self) => {
            if (video.duration && !isNaN(video.duration)) {
              video.currentTime = video.duration * self.progress;
            }

            const progress = self.progress;
            let newIndex;
            if (progress < 0.33) {
              newIndex = 0;
            } else if (progress < 0.66) {
              newIndex = 1;
            } else {
              newIndex = 2;
            }

            if (newIndex !== currentIndex) {
              setCurrentIndex(newIndex);
            }
          },
        });
      });

      ScrollTrigger.refresh();
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    if (video.readyState >= 1) {
      handleLoadedMetadata();
    }

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      if (ctx) {
        ctx.revert();
      }
    };
  }, [currentIndex]);

  useEffect(() => {
    if (textRef.current) {
      gsap.fromTo(
        textRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }
      );
    }
  }, [currentIndex]);

  // Simple scroll down
  const handleScrollDown = (e) => {
    e && e.preventDefault && e.preventDefault();
    e && e.stopPropagation && e.stopPropagation();

    // if gsap/ScrollTrigger is available, temporarily disable it
    try {
      if (ScrollTrigger && typeof ScrollTrigger.disable === "function") {
        ScrollTrigger.disable();
      }
    } catch (err) {
      // ignore if not available
    }

    // compute the target Y: the bottom of the container (so we scroll past the pin)
    const container = containerRef.current;
    const nextSection = document.querySelector("#testimonials");

    let targetY = 0;
    if (nextSection) {
      // prefer scrolling to the next section top (safer)
      targetY = nextSection.getBoundingClientRect().top + window.pageYOffset;
    } else if (container) {
      // otherwise scroll just past the container
      targetY =
        container.getBoundingClientRect().top +
        window.pageYOffset +
        container.offsetHeight +
        2;
    } else {
      // fallback: scroll one viewport down
      targetY = window.pageYOffset + window.innerHeight;
    }

    // perform smooth scroll
    window.scrollTo({
      top: Math.round(targetY),
      behavior: "smooth",
    });

    // re-enable ScrollTrigger after the smooth scroll is expected to finish
    // (adjust timeout if your page/animation is slower)
    const REENABLE_MS = 900;
    setTimeout(() => {
      try {
        if (ScrollTrigger && typeof ScrollTrigger.enable === "function") {
          ScrollTrigger.enable();
          // refresh to ensure positions are updated
          ScrollTrigger.refresh();
        }
      } catch (err) {
        // ignore
      }
    }, REENABLE_MS);
  };

  return (
    <>
      <div ref={containerRef} className="video-scroll-container">
        <div className="video-wrapper">
          <video
            ref={videoRef}
            className="scroll-video"
            src={getFileUrl(intro.videoFile)}
            muted
            playsInline
            preload="metadata"
          />

          <div ref={textRef} className="header-text-content">
            {currentIndex === 0 && (
              <h4 className="text-salute">{intro?.salute}</h4>
            )}
            {currentIndex === 1 && <h1 className="text-name">{intro?.name}</h1>}
            {currentIndex === 2 && <div className="text-job">{intro?.job}</div>}
          </div>

          <div className="header-socials-wrapper">
            <HeaderSocials links={intro} />
          </div>

          <button
            className="scroll-down-link"
            onClick={handleScrollDown}
            type="button"
          >
            <h4>Scroll Down</h4>
          </button>
        </div>
      </div>
    </>
  );
};

export default Header;
